<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useSportSessions } from '@/composables/useSportSessions';
import ParticipantBadges from "./ParticipantBadges.vue";

const props = defineProps<{
    config: { activeProviders: string[]; } | null;
}>();

const emit = defineEmits<{
    (e: 'switch-tab', tab: 'config' | 'sessions'): void
}>();

const { 
    sessions, 
    sessionsLoading, 
    sessionsError, 
    isSyncing, 
    loadSessions, 
    refreshSessions 
} = useSportSessions();

const sessionsSubTab = ref<'upcoming' | 'past'>('upcoming');

onMounted(() => {
    loadSessions();
});

const formatDate = (iso: string): string => {
    return new Date(iso).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
};

const formatTime = (iso: string): string => {
    return new Date(iso).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatDuration = (startIso: string, endIso: string): string => {
    const diffMs = new Date(endIso).getTime() - new Date(startIso).getTime();
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 60) return `${diffMin} min`;
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    return m > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${h}h`;
};
</script>

<template>
    <div>
        <!-- Loading -->
        <div v-if="sessionsLoading" class="text-center py-5">
            <div class="spinner-border text-dark" role="status">
                <span class="visually-hidden">Chargement des sessions…</span>
            </div>
            <p class="text-muted mt-3 small">Récupération de vos sessions auprès des providers…</p>
        </div>

        <!-- Error -->
        <div v-else-if="sessionsError" class="text-center py-5">
            <div class="sessions-empty-icon mb-3">
                <i class="fas fa-exclamation-circle text-danger" style="font-size: 3rem;"></i>
            </div>
            <p class="text-muted">Impossible de charger vos sessions.</p>
            <button @click="refreshSessions" class="btn btn-dark btn-sm rounded-pill px-4">
                <i class="fas fa-redo me-2"></i>Réessayer
            </button>
        </div>

        <!-- Sessions content -->
        <div v-else-if="sessions">
            <!-- No provider configured -->
            <div v-if="!config?.activeProviders.length" class="sessions-empty-state text-center py-5">
                <div class="sessions-empty-icon mb-3">
                    <i class="fas fa-plug" style="font-size: 3rem; color: #a0aec0;"></i>
                </div>
                <p class="text-muted mb-3">Aucun provider configuré.</p>
                <button @click="emit('switch-tab', 'config')" class="btn btn-dark btn-sm rounded-pill px-4">
                    Configurer mes comptes
                </button>
            </div>

            <template v-else>
                <!-- Sub-tabs: À venir / Historique -->
                <div class="sessions-subtabs mb-4">
                    <button
                        class="subtab-btn"
                        :class="{ active: sessionsSubTab === 'upcoming' }"
                        @click="sessionsSubTab = 'upcoming'"
                    >
                        <i class="fas fa-clock me-2"></i>
                        À venir
                        <span class="subtab-count">{{ sessions.upcoming.length }}</span>
                    </button>
                    <button
                        class="subtab-btn"
                        :class="{ active: sessionsSubTab === 'past' }"
                        @click="sessionsSubTab = 'past'"
                    >
                        <i class="fas fa-history me-2"></i>
                        Historique
                        <span class="subtab-count">{{ sessions.past.length }}</span>
                    </button>

                    <button @click="refreshSessions" :disabled="isSyncing" class="refresh-btn ms-auto" title="Rafraîchir">
                        <i class="fas" :class="isSyncing ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                    </button>
                </div>

                <!-- Upcoming sessions -->
                <div v-if="sessionsSubTab === 'upcoming'">
                    <div v-if="sessions.upcoming.length === 0" class="sessions-empty-state text-center py-5">
                        <div class="sessions-empty-icon mb-3">
                            <i class="fas fa-calendar-check" style="font-size: 3rem; color: #a0aec0;"></i>
                        </div>
                        <p class="text-muted">Aucune session à venir pour le moment.</p>
                    </div>
                    <div v-else class="sessions-list">
                        <div v-for="session in sessions.upcoming" :key="session.uid" class="session-card upcoming">
                            <div class="session-card-accent"></div>
                            <div class="session-card-body">
                                <div class="session-header">
                                    <div>
                                        <div class="session-title">{{ session.title }}</div>
                                        <div class="session-date">
                                            <i class="fas fa-calendar me-1"></i>
                                            {{ formatDate(session.start) }}
                                        </div>
                                    </div>
                                    <span class="provider-badge" :class="session.provider">
                                        {{ session.provider === 'episod' ? 'Episod' : 'Sanctuary' }}
                                    </span>
                                </div>
                                <div class="session-meta">
                                    <span class="session-meta-item">
                                        <i class="fas fa-clock"></i>
                                        {{ formatTime(session.start) }}
                                    </span>
                                    <span class="session-meta-item">
                                        <i class="fas fa-stopwatch"></i>
                                        {{ formatDuration(session.start, session.end) }}
                                    </span>
                                    <div v-if="session.participants && session.participants.length > 0" class="ms-auto d-flex align-items-center">
                                        <ParticipantBadges 
                                            :participants="session.participants"
                                            size="md"
                                            :showText="false"
                                        />
                                    </div>
                                </div>
                                <div v-if="session.cancelUrl" class="session-footer">
                                    <a :href="session.cancelUrl" target="_blank" rel="noopener" class="session-cancel-link">
                                        <i class="fas fa-times-circle me-1"></i>Annuler la réservation
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Past sessions -->
                <div v-else-if="sessionsSubTab === 'past'">
                    <div v-if="sessions.past.length === 0" class="sessions-empty-state text-center py-5">
                        <div class="sessions-empty-icon mb-3">
                            <i class="fas fa-history" style="font-size: 3rem; color: #a0aec0;"></i>
                        </div>
                        <p class="text-muted">Aucun historique de session disponible.</p>
                    </div>
                    <div v-else class="sessions-list">
                        <div v-for="session in sessions.past" :key="session.uid" class="session-card past">
                            <div class="session-card-accent"></div>
                            <div class="session-card-body">
                                <div class="session-header">
                                    <div>
                                        <div class="session-title">{{ session.title }}</div>
                                        <div class="session-date">
                                            <i class="fas fa-calendar me-1"></i>
                                            {{ formatDate(session.start) }}
                                        </div>
                                    </div>
                                    <span class="provider-badge" :class="session.provider">
                                        {{ session.provider === 'episod' ? 'Episod' : 'Sanctuary' }}
                                    </span>
                                </div>
                                <div class="session-meta">
                                    <span class="session-meta-item">
                                        <i class="fas fa-clock"></i>
                                        {{ formatTime(session.start) }}
                                    </span>
                                    <span class="session-meta-item">
                                        <i class="fas fa-stopwatch"></i>
                                        {{ formatDuration(session.start, session.end) }}
                                    </span>
                                    <div v-if="session.participants && session.participants.length > 0" class="ms-auto d-flex align-items-center">
                                        <ParticipantBadges 
                                            :participants="session.participants"
                                            size="md"
                                            :showText="false"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped>
/* ---- Sub-tabs ---- */
.sessions-subtabs {
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 2px solid #f0f0f0;
    padding-bottom: 0;
}
.subtab-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    margin-bottom: -2px;
    padding: 0.6rem 1rem;
    font-weight: 600;
    font-size: 0.88rem;
    color: #6c757d;
    cursor: pointer;
    transition: all 0.2s ease;
}
.subtab-btn:hover {
    color: #212529;
}
.subtab-btn.active {
    color: #212529;
    border-bottom-color: #212529;
}
.subtab-count {
    background: #e9ecef;
    color: #495057;
    border-radius: 20px;
    padding: 1px 8px;
    font-size: 0.75rem;
    font-weight: 700;
}
.subtab-btn.active .subtab-count {
    background: #212529;
    color: white;
}
.refresh-btn {
    background: none;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 6px 10px;
    color: #6c757d;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.85rem;
}
.refresh-btn:hover {
    background: #f8f9fa;
    color: #212529;
    border-color: #adb5bd;
}

/* ---- Session cards ---- */
.sessions-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.session-card {
    display: flex;
    border-radius: 14px;
    overflow: hidden;
    background: white;
    box-shadow: 0 1px 4px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.04);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.session-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.06);
}
.session-card-accent {
    width: 5px;
    flex-shrink: 0;
}
.session-card.upcoming .session-card-accent {
    background: linear-gradient(180deg, #ff9f43, #ff6b6b);
}
.session-card.past .session-card-accent {
    background: #dee2e6;
}
.session-card.past {
    opacity: 0.8;
}
.session-card-body {
    flex: 1;
    padding: 1rem 1.25rem;
}
.session-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 10px;
}
.session-title {
    font-weight: 700;
    font-size: 1rem;
    color: #212529;
    line-height: 1.3;
    margin-bottom: 3px;
}
.session-date {
    font-size: 0.82rem;
    color: #6c757d;
}

/* ---- Provider badges ---- */
.provider-badge {
    flex-shrink: 0;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-radius: 6px;
    padding: 3px 8px;
}
.provider-badge.episod {
    background: rgba(255, 159, 67, 0.12);
    color: #e07b00;
    border: 1px solid rgba(255, 159, 67, 0.3);
}
.provider-badge.sanctuary {
    background: rgba(79, 172, 254, 0.12);
    color: #1d7fc4;
    border: 1px solid rgba(79, 172, 254, 0.3);
}

/* ---- Session meta ---- */
.session-meta {
    display: flex;
    gap: 1rem;
}
.session-meta-item {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.82rem;
    color: #6c757d;
}
.session-meta-item i {
    font-size: 0.75rem;
    opacity: 0.7;
}

/* ---- Cancel link ---- */
.session-footer {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #f0f0f0;
}
.session-cancel-link {
    font-size: 0.8rem;
    color: #dc3545;
    text-decoration: none;
    font-weight: 600;
    transition: opacity 0.2s;
}
.session-cancel-link:hover {
    opacity: 0.75;
    text-decoration: underline;
}

/* ---- Empty states ---- */
.sessions-empty-state {
    background: #f8f9fa;
    border-radius: 16px;
    padding: 3rem;
}
</style>
